import React, { useEffect, useMemo, useState } from 'react';
import './DashboardPage.css';
import dashboardApi from '../../../services/apiServices/dashboardApi';
import orderApi from '../../../services/apiServices/orderApi';
import configService from '../../../services/configService';
import {
    addDays,
    addMonths,
    endOfMonth,
    format,
    isAfter,
    isSameMonth,
    startOfMonth,
    subMonths
} from "date-fns";
import { Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../services/redux/loadingSlice';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from "react-router-dom";

const DashboardPage = () => {
    const [metrics, setMetrics] = useState({});
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [periodMode, setPeriodMode] = useState('month');
    const [customStartDate, setCustomStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
    const [customEndDate, setCustomEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [chartView, setChartView] = useState('created');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const period = useMemo(() => {
        const today = new Date();

        if (periodMode === 'custom') {
            return {
                startDate: customStartDate,
                endDate: customEndDate,
                label: 'Período personalizado'
            };
        }

        const startDate = format(startOfMonth(currentMonth), "yyyy-MM-dd");
        const endDate = format(isSameMonth(currentMonth, today) ? today : endOfMonth(currentMonth), "yyyy-MM-dd");

        return {
            startDate,
            endDate,
            label: format(currentMonth, "MMMM yyyy", { locale: ptBR })
        };
    }, [currentMonth, periodMode, customStartDate, customEndDate]);

    useEffect(() => {
        const fetchMetrics = async () => {
            dispatch(setLoading(true));
            setIsLoadingMetrics(true);
            setHasError(false);

            try {
                const metricsData = await dashboardApi.getMetrics({
                    startDate: period.startDate,
                    endDate: period.endDate
                });
                setMetrics(metricsData || {});
            } catch (error) {
                console.error("Erro ao buscar métricas:", error);
                setHasError(true);
                toast.error('Erro ao buscar os dados do dashboard.');
            } finally {
                dispatch(setLoading(false));
                setIsLoadingMetrics(false);
            }
        };

        fetchMetrics();
    }, [period.startDate, period.endDate, dispatch]);

    const handlePreviousMonth = () => {
        setPeriodMode('month');
        setCurrentMonth(prev => subMonths(prev, 1));
    };

    const handleNextMonth = () => {
        const next = addMonths(currentMonth, 1);
        if (!isAfter(next, new Date())) {
            setPeriodMode('month');
            setCurrentMonth(next);
        }
    };

    const applyCustomPeriod = () => {
        if (!customStartDate || !customEndDate) {
            toast.error('Informe a data inicial e a data final.');
            return;
        }

        if (new Date(`${customStartDate}T00:00:00`) > new Date(`${customEndDate}T00:00:00`)) {
            toast.error('A data inicial não pode ser maior que a data final.');
            return;
        }

        setPeriodMode('custom');
    };

    const hasMetric = (key) => Object.prototype.hasOwnProperty.call(metrics, key) && metrics[key] !== null && metrics[key] !== undefined;
    const getMetric = (key) => hasMetric(key) ? metrics[key] : 0;
    const getOptionalMetric = (key) => hasMetric(key) ? metrics[key] : null;

    const formatNumber = (value) => new Intl.NumberFormat('pt-BR').format(Number(value || 0));
    const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));

    const buildOrdersPath = (statusId) => {
        const params = new URLSearchParams({
            startDate: period.startDate,
            endDate: period.endDate
        });

        if (statusId !== undefined && statusId !== null) {
            params.set('status', statusId);
        }

        return `/pedidos?${params.toString()}`;
    };

    const buildMailPath = (status) => {
        const params = new URLSearchParams({
            startDate: period.startDate,
            endDate: period.endDate
        });
        if (status) {
            params.set('status', status);
        }
        return `/emails?${params.toString()}`;
    };

    const buildIntegrationLogsPath = () => `/logs-integration?startDate=${period.startDate}&endDate=${period.endDate}`;

    const exportOrdersReport = async () => {
        try {
            dispatch(setLoading(true));
            const response = await orderApi.exportOrders({
                startDate: period.startDate,
                endDate: period.endDate
            });

            if (response.Status === 200 && response.Object) {
                window.open(response.Object, "_blank");
                toast.success('Relatório gerado com sucesso!');
            } else {
                toast.error('Erro ao gerar o relatório');
            }
        } catch (error) {
            toast.error('Erro ao gerar o relatório');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const summaryCards = [
        {
            label: 'Total de pedidos',
            value: formatNumber(getMetric('QuantityOrders')),
            helper: 'Pedidos criados no período selecionado.',
            to: buildOrdersPath()
        },
        {
            label: 'Pedidos pagos',
            value: formatNumber(getMetric('QuantityOrdersPaid')),
            helper: 'Pedidos com pagamento confirmado.',
            tone: 'success',
            to: buildOrdersPath(configService.getOrderStatusPaid())
        },
        {
            label: 'Valor pago',
            value: formatCurrency(getOptionalMetric('PaidOrderValue')),
            helper: 'Total financeiro pago no período.',
            tone: 'success',
            isUnavailable: !hasMetric('PaidOrderValue')
        }
    ];

    const sections = [
        {
            title: 'Receita / Vendas',
            description: 'Resultados comerciais e avanço de pagamento no período.',
            cards: [
                {
                    label: 'Pedidos pagos',
                    value: formatNumber(getMetric('QuantityOrdersPaid')),
                    helper: 'Pedidos concluídos com pagamento confirmado.',
                    tone: 'success',
                    to: buildOrdersPath(configService.getOrderStatusPaid())
                },
                {
                    label: 'Pedidos permitidos para pagamento',
                    value: formatNumber(getMetric('QuantityOrdersAllowPayment')),
                    helper: 'Pedidos ainda aptos a receber pagamento.',
                    tone: 'warning',
                    to: buildOrdersPath()
                },
                {
                    label: 'Valor total pago',
                    value: formatCurrency(getOptionalMetric('PaidOrderValue')),
                    helper: 'Soma dos valores pagos nas transações concluídas do período.',
                    tone: 'success',
                    isUnavailable: !hasMetric('PaidOrderValue')
                },
                {
                    label: 'Ticket médio',
                    value: formatCurrency(getOptionalMetric('AverageTicket')),
                    helper: 'Valor médio dos pedidos pagos no período selecionado.',
                    isUnavailable: !hasMetric('AverageTicket')
                }
            ]
        },
        {
            title: 'Atenção Operacional',
            description: 'Pedidos que podem exigir acompanhamento ou ação.',
            cards: [
                {
                    label: 'Aguardando pagamento',
                    value: formatNumber(getMetric('QuantityOrdersWaitPayment')),
                    helper: 'Pedidos pendentes de confirmação de pagamento.',
                    tone: 'warning',
                    to: buildOrdersPath(configService.getOrderStatusWaitPayment())
                },
                {
                    label: 'Em análise',
                    value: formatNumber(getMetric('QuantityUnderAnalysis')),
                    helper: 'Pedidos ainda em validação operacional.',
                    tone: 'warning',
                    to: buildOrdersPath(configService.getOrderStatusUnderAnalysis())
                },
                {
                    label: 'Expirados',
                    value: formatNumber(getMetric('QuantityOrdersExpired')),
                    helper: 'Pedidos que passaram da data limite de pagamento.',
                    tone: 'critical',
                    to: buildOrdersPath(configService.getOrderStatusExpired())
                },
                {
                    label: 'Próximos da expiração',
                    value: formatNumber((getOptionalMetric('OrdersCloseToExpiration') || []).length),
                    helper: 'Pedidos com pagamento permitido e prazo de expiração próximo.',
                    tone: 'warning',
                    isUnavailable: !hasMetric('OrdersCloseToExpiration')
                }
            ]
        },
        {
            title: 'Volume',
            description: 'Tamanho da operação no período e base cadastrada.',
            cards: [
                {
                    label: 'Total de pedidos',
                    value: formatNumber(getMetric('QuantityOrders')),
                    helper: 'Número total de pedidos registrados.',
                    to: buildOrdersPath()
                },
                {
                    label: 'Total de clientes',
                    value: formatNumber(getMetric('QuantityClients')),
                    helper: 'Clientes cadastrados no período selecionado.',
                    to: '/usuarios'
                },
                {
                    label: 'Cancelados pelo cliente',
                    value: formatNumber(getMetric('QuantityOrdersCanceledCustomer')),
                    helper: 'Pedidos cancelados por ação do cliente.',
                    tone: 'warning',
                    to: buildOrdersPath(configService.getOrderStatusCanceledCustomer())
                },
                {
                    label: 'Cancelados pela loja',
                    value: formatNumber(getMetric('QuantityOrdersCanceledStore')),
                    helper: 'Pedidos cancelados por ação operacional da loja.',
                    tone: 'warning',
                    to: buildOrdersPath(configService.getOrderStatusCanceledStore())
                }
            ]
        },
        {
            title: 'Confiabilidade',
            description: 'Saúde de pagamentos, e-mails e integrações.',
            cards: [
                {
                    label: 'Transações concluídas',
                    value: formatNumber(getMetric('QuantityTransactionsOk')),
                    helper: 'Transações finalizadas com sucesso.',
                    tone: 'success'
                },
                {
                    label: 'Transações não concluídas',
                    value: formatNumber(getMetric('QuantityTransactionsNotOk')),
                    helper: 'Transações que não foram concluídas com sucesso.',
                    tone: getMetric('QuantityTransactionsNotOk') > 0 ? 'critical' : 'success'
                },
                {
                    label: 'E-mails enviados',
                    value: formatNumber(getMetric('QuantityMailsSent')),
                    helper: 'E-mails disparados no período.',
                    to: buildMailPath('Sent')
                },
                {
                    label: 'E-mails não enviados',
                    value: formatNumber(getMetric('QuantityMailsNotSent')),
                    helper: 'E-mails que ainda não foram enviados com sucesso no período.',
                    tone: hasMetric('QuantityMailsNotSent') && getMetric('QuantityMailsNotSent') > 0 ? 'critical' : 'success',
                    to: hasMetric('QuantityMailsNotSent') ? buildMailPath('NotSent') : undefined,
                    isUnavailable: !hasMetric('QuantityMailsNotSent')
                },
                {
                    label: 'Erros de integração',
                    value: formatNumber(getMetric('QuantityIntegrationErrors')),
                    helper: 'Registros de integração com indícios de erro ou falha.',
                    tone: hasMetric('QuantityIntegrationErrors') && getMetric('QuantityIntegrationErrors') > 0 ? 'critical' : 'success',
                    to: hasMetric('QuantityIntegrationErrors') ? buildIntegrationLogsPath() : undefined,
                    isUnavailable: !hasMetric('QuantityIntegrationErrors')
                }
            ]
        }
    ];

    const attentionGroups = [
        {
            title: 'Pedidos em análise',
            items: getOptionalMetric('RecentUnderAnalysisOrders') || [],
            empty: 'Nenhum pedido em análise retornado neste período.',
            route: buildOrdersPath(configService.getOrderStatusUnderAnalysis())
        },
        {
            title: 'Falhas de pagamento',
            items: getOptionalMetric('RecentFailedTransactions') || [],
            empty: 'Nenhuma falha de pagamento retornada neste período.'
        },
        {
            title: 'E-mails não enviados',
            items: getOptionalMetric('RecentUnsentEmails') || [],
            empty: 'Nenhum e-mail pendente retornado neste período.',
            route: buildMailPath('NotSent')
        },
        {
            title: 'Erros de integração',
            items: getOptionalMetric('RecentIntegrationErrors') || [],
            empty: 'Nenhum erro de integração retornado neste período.',
            route: buildIntegrationLogsPath()
        },
        {
            title: 'Pedidos próximos da expiração',
            items: getOptionalMetric('OrdersCloseToExpiration') || [],
            empty: 'Nenhum pedido próximo da expiração retornado neste período.'
        }
    ];

    const hasAnyAttentionList = attentionGroups.some(group => group.items.length > 0);
    const hasAnyKnownProblem =
        getMetric('QuantityTransactionsNotOk') > 0 ||
        getMetric('QuantityOrdersExpired') > 0 ||
        getMetric('QuantityUnderAnalysis') > 0 ||
        (hasMetric('QuantityMailsNotSent') && getMetric('QuantityMailsNotSent') > 0) ||
        (hasMetric('QuantityIntegrationErrors') && getMetric('QuantityIntegrationErrors') > 0);

    const formatLabel = (items) => items.map(item => {
        const parsedDate = addDays(new Date(`${item}T00:00:00`), 0);

        if (isNaN(parsedDate.getTime())) {
            return '';
        }

        return format(parsedDate, 'dd/MM/yyyy');
    });

    const statusSeries = metrics.OrdersByStatusPerDay || {};
    const availableChartViews = [
        {
            key: 'created',
            label: 'Pedidos criados',
            values: metrics.QuantityOrdersPerDay,
            color: '#1E293B'
        },
        {
            key: 'paid',
            label: 'Pagos',
            values: statusSeries.Paid || statusSeries.paid,
            color: '#2E7D32'
        },
        {
            key: 'canceled',
            label: 'Cancelados / expirados',
            values: statusSeries.CanceledOrExpired || statusSeries.canceledOrExpired,
            color: '#D97706'
        },
        {
            key: 'failures',
            label: 'Falhas',
            values: statusSeries.Failures || statusSeries.failures,
            color: '#B00020'
        }
    ].filter(view => view.values && Object.keys(view.values).length > 0);

    const selectedChart = availableChartViews.find(view => view.key === chartView) || availableChartViews[0];
    const chartLabels = selectedChart ? formatLabel(Object.keys(selectedChart.values)) : [];
    const chartValues = selectedChart ? Object.values(selectedChart.values) : [];
    const chartData = {
        labels: chartLabels,
        datasets: [
            {
                label: selectedChart?.label || 'Pedidos criados',
                data: chartValues,
                backgroundColor: selectedChart?.color || '#1E293B',
                borderColor: selectedChart?.color || '#1E293B',
                borderWidth: 1,
                borderRadius: 4
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    title: (items) => items?.[0]?.label || '',
                    label: (item) => `${selectedChart?.label || 'Total'}: ${formatNumber(item.raw)}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    const orderStatusChartItems = [
        {
            label: 'Aguardando pagamento',
            value: getMetric('QuantityOrdersWaitPayment'),
            color: '#D97706'
        },
        {
            label: 'Pagos',
            value: getMetric('QuantityOrdersPaid'),
            color: '#2E7D32'
        },
        {
            label: 'Cancelados pelo cliente',
            value: getMetric('QuantityOrdersCanceledCustomer'),
            color: '#F59E0B'
        },
        {
            label: 'Cancelados pela loja',
            value: getMetric('QuantityOrdersCanceledStore'),
            color: '#B45309'
        },
        {
            label: 'Expirados',
            value: getMetric('QuantityOrdersExpired'),
            color: '#B00020'
        },
        {
            label: 'Em análise',
            value: getMetric('QuantityUnderAnalysis'),
            color: '#2563EB'
        }
    ].filter(item => item.value > 0);

    const orderStatusChartData = {
        labels: orderStatusChartItems.map(item => item.label),
        datasets: [
            {
                data: orderStatusChartItems.map(item => item.value),
                backgroundColor: orderStatusChartItems.map(item => item.color),
                borderColor: '#ffffff',
                borderWidth: 2
            }
        ]
    };

    const orderStatusChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    boxWidth: 12,
                    usePointStyle: true
                }
            },
            tooltip: {
                callbacks: {
                    label: (item) => `${item.label}: ${formatNumber(item.raw)}`
                }
            }
        },
        cutout: '62%'
    };

    const pendingChartData = {
        labels: ['Transações não concluídas', 'E-mails não enviados', 'Erros de integração', 'Pedidos expirados', 'Em análise'],
        datasets: [
            {
                label: 'Pendências',
                data: [
                    getMetric('QuantityTransactionsNotOk'),
                    getMetric('QuantityMailsNotSent'),
                    getMetric('QuantityIntegrationErrors'),
                    getMetric('QuantityOrdersExpired'),
                    getMetric('QuantityUnderAnalysis')
                ],
                backgroundColor: ['#B00020', '#C2410C', '#7F1D1D', '#D97706', '#2563EB'],
                borderRadius: 4
            }
        ]
    };

    const pendingChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: (item) => `${item.label}: ${formatNumber(item.raw)}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    const goTo = (path) => {
        if (path) {
            navigate(path);
        }
    };

    if (isLoadingMetrics) {
        return (
            <div className="container-admin-page">
                <h1>Dashboard</h1>
                <div className="dashboard-skeleton-grid">
                    {Array.from({ length: 8 }).map((_, index) => (
                        <div key={index} className="dashboard-skeleton-card" />
                    ))}
                </div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="container-admin-page">
                <h1>Dashboard</h1>
                <div className="dashboard-error">
                    <h2>Não foi possível carregar o dashboard</h2>
                    <p>Verifique sua conexão ou tente atualizar os dados novamente.</p>
                    <button className="main-button" onClick={() => window.location.reload()}>Tentar novamente</button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-admin-page dashboard-page">
            <div className="dashboard-header">
                <div>
                    <h1>Dashboard</h1>
                    <p>Período: <strong>{period.label}</strong> ({format(addDays(new Date(`${period.startDate}T00:00:00`), 0), 'dd/MM/yyyy')} até {format(addDays(new Date(`${period.endDate}T00:00:00`), 0), 'dd/MM/yyyy')})</p>
                </div>
                <div className="dashboard-actions">
                    <button className="main-button" onClick={() => goTo(buildOrdersPath())}>Listar pedidos</button>
                    <button className="main-button" onClick={() => goTo('/emails')}>Listar e-mails</button>
                    <button className="main-button" onClick={() => goTo(buildIntegrationLogsPath())}>Listar logs de integração</button>
                    <button className="main-button" onClick={exportOrdersReport}>Exportar relatório</button>
                </div>
            </div>

            <div className="dashboard-filter">
                <button className="month-button" onClick={handlePreviousMonth}>Anterior</button>
                <span className="selected-month">{period.label}</span>
                <button className="month-button" onClick={handleNextMonth} disabled={periodMode === 'month' && isSameMonth(currentMonth, new Date())}>Próximo</button>
                <div className="custom-period-filter">
                    <label>
                        Inicial
                        <input
                            type="date"
                            value={customStartDate}
                            onChange={(event) => setCustomStartDate(event.target.value)}
                        />
                    </label>
                    <label>
                        Final
                        <input
                            type="date"
                            value={customEndDate}
                            onChange={(event) => setCustomEndDate(event.target.value)}
                        />
                    </label>
                    <button className={periodMode === 'custom' ? 'period-shortcut active' : 'period-shortcut'} onClick={applyCustomPeriod}>Aplicar período</button>
                </div>
            </div>

            <div className="dashboard-summary-header">
                {summaryCards.map(card => (
                    <button
                        key={card.label}
                        className={`dashboard-summary-card ${card.tone || 'neutral'} ${card.to ? 'clickable-card' : ''} ${card.isUnavailable ? 'is-unavailable' : ''}`}
                        onClick={() => goTo(card.to)}
                        disabled={!card.to}
                        title={card.helper}
                    >
                        <span>{card.label}</span>
                        <strong>{card.value}</strong>
                    </button>
                ))}
            </div>

            <section className="needs-attention">
                <div className="dashboard-section-title">
                    <div>
                        <h2>Precisa de Atenção</h2>
                        <p>Pedidos, pagamentos, e-mails e integrações que merecem acompanhamento.</p>
                    </div>
                </div>

                {!hasAnyAttentionList && !hasAnyKnownProblem && (
                    <div className="positive-empty-state">Nenhuma pendência encontrada neste período.</div>
                )}

                {!hasAnyAttentionList && hasAnyKnownProblem && (
                    <div className="dashboard-empty-state">
                        Existem métricas que merecem atenção neste período.
                    </div>
                )}

                <div className="attention-grid">
                    {attentionGroups.map(group => (
                        <div key={group.title} className="attention-card">
                            <div className="attention-card-header">
                                <h3>{group.title}</h3>
                                {group.route && <button onClick={() => goTo(group.route)}>Abrir</button>}
                            </div>
                            {group.items.length > 0 ? (
                                <ul>
                                    {group.items.slice(0, 5).map((item, index) => (
                                        <li key={item.Id || item.id || index}>
                                            <strong>{item.NrOrder || item.OrderId || item.Subject || item.Id || item.id || 'Item'}</strong>
                                            <span>{item.Description || item.Email || item.To || item.Status || item.TextLog || 'Detalhe não informado'}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>{group.empty}</p>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            <section className="dashboard-visual-grid">
                <div className="dashboard-chart dashboard-chart-compact">
                    <div className="dashboard-chart-header">
                        <div>
                            <h2>Pedidos por status</h2>
                            <p>Distribuição dos pedidos no período selecionado.</p>
                        </div>
                    </div>
                    {orderStatusChartItems.length > 0 ? (
                        <div className="chart-frame compact">
                            <Doughnut data={orderStatusChartData} options={orderStatusChartOptions} />
                        </div>
                    ) : (
                        <div className="dashboard-empty-state">Nenhum pedido encontrado para este período.</div>
                    )}
                </div>

                <div className="dashboard-chart dashboard-chart-compact">
                    <div className="dashboard-chart-header">
                        <div>
                            <h2>Pendências operacionais</h2>
                            <p>Principais pontos que podem exigir acompanhamento.</p>
                        </div>
                    </div>
                    <div className="chart-frame compact">
                        <Bar data={pendingChartData} options={pendingChartOptions} />
                    </div>
                </div>
            </section>

            {sections.map(section => (
                <section key={section.title} className="dashboard-section">
                    <div className="dashboard-section-title">
                        <div>
                            <h2>{section.title}</h2>
                            <p>{section.description}</p>
                        </div>
                    </div>
                    <div className="dashboard-card-grid">
                        {section.cards.map(card => (
                            <button
                                key={card.label}
                                className={`metric-card ${card.tone || 'neutral'} ${card.to ? 'clickable-card' : ''} ${card.isUnavailable ? 'is-unavailable' : ''}`}
                                onClick={() => goTo(card.to)}
                                disabled={!card.to}
                                title={card.helper}
                            >
                                <span>{card.label}</span>
                                <strong>{card.value}</strong>
                                <small>{card.helper}</small>
                            </button>
                        ))}
                    </div>
                </section>
            ))}

            <section className="dashboard-chart">
                <div className="dashboard-chart-header">
                    <div>
                        <h2>Pedidos por dia</h2>
                        <p>Acompanhe a evolução diária dos pedidos e eventos do período.</p>
                    </div>
                    {availableChartViews.length > 1 && (
                        <div className="chart-switcher">
                            {availableChartViews.map(view => (
                                <button
                                    key={view.key}
                                    className={selectedChart?.key === view.key ? 'active' : ''}
                                    onClick={() => setChartView(view.key)}
                                >
                                    {view.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {selectedChart ? (
                    <div className="chart-frame">
                        <Bar data={chartData} options={chartOptions} />
                    </div>
                ) : (
                    <div className="dashboard-empty-state">Nenhum dado diário retornado para este período.</div>
                )}
            </section>
        </div>
    );
};

export default DashboardPage;
